/**
 * Workplan Generation Modal
 * AI-powered workplan generation using past projects, technical approaches, and compliance requirements
 * Phase 5: Core workplan generation feature
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Badge, ReportJobMonitor } from '@tracekit/core';
import { FileText, X, Loader, CheckCircle, AlertCircle, Lightbulb, Shield } from 'lucide-react';
import { useWorkspace } from '@tracekit/core';
import { useWorkplanJobSubmit, useWorkplanJobStatus, getWorkplanTemplate } from '@tracekit/core';

interface PastProjectEntity {
  id: string;
  entity_type: string;
  data: {
    project_name: string;
    client_name: string;
    project_type?: string;
    service_line?: string;
    scope_description?: string;
    [key: string]: any;
  };
}

interface TechnicalApproachEntity {
  id: string;
  entity_type: string;
  data: {
    approach_name: string;
    methodology_summary?: string;
    [key: string]: any;
  };
}

interface ComplianceEntity {
  id: string;
  entity_type: string;
  data: {
    [key: string]: any;
  };
}

interface WorkplanGenerationModalProps {
  project: PastProjectEntity;
  selectedApproaches: TechnicalApproachEntity[];
  selectedCompliance: ComplianceEntity[];
  onClose: () => void;
  onGenerate: (reportId: string) => void;
}

export function WorkplanGenerationModal({
  project,
  selectedApproaches,
  selectedCompliance,
  onClose,
  onGenerate
}: WorkplanGenerationModalProps) {
  const [clientName, setClientName] = useState(project.data.client_name || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!clientName.trim()) {
      setError('Please enter a client name');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // TODO: Phase 5 - This will call the backend API endpoint for workplan generation
      // For now, we'll show a placeholder
      console.log('[WorkplanGenerationModal] Generating workplan:', {
        past_project_id: project.id,
        client_name: clientName,
        approach_ids: selectedApproaches.map(a => a.id),
        compliance_ids: selectedCompliance.map(c => c.id),
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock report ID
      const mockReportId = `report-${Date.now()}`;
      
      onGenerate(mockReportId);
      onClose();
    } catch (err) {
      console.error('[WorkplanGenerationModal] Generation failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate workplan');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="space-y-6 p-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Generate Workplan
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={isGenerating}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Info Banner */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              AI will generate a comprehensive workplan based on:
            </h3>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Project:</strong> {project.data.project_name}
                  {project.data.service_line && (
                    <Badge variant="outline" className="ml-2">{project.data.service_line}</Badge>
                  )}
                </div>
              </li>
              {selectedApproaches.length > 0 && (
                <li className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Technical Approaches:</strong> {selectedApproaches.length} selected
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedApproaches.map(approach => (
                        <Badge key={approach.id} variant="secondary" className="text-xs">
                          {approach.data.approach_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </li>
              )}
              {selectedCompliance.length > 0 && (
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Compliance Requirements:</strong> {selectedCompliance.length} selected
                  </div>
                </li>
              )}
              <li className="flex items-start gap-2">
                <FileText className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Similar Past Workplans:</strong> AI will retrieve relevant examples
                </div>
              </li>
            </ul>
          </div>

          {/* Client Name Input */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Client Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
              disabled={isGenerating}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be used in the generated workplan document
            </p>
          </div>

          {/* Project Details */}
          {project.data.scope_description && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Project Scope
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
                {project.data.scope_description}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button 
              variant="outline" 
              onClick={onClose} 
              disabled={isGenerating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={!clientName.trim() || isGenerating}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Workplan
                </>
              )}
            </Button>
          </div>

          {/* Job Monitoring */}
          {submittedJobId && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <ReportJobMonitor
                jobId={submittedJobId}
                onComplete={(reportId) => {
                  onGenerate(reportId);
                  onClose();
                }}
                onViewReport={(reportId) => {
                  // Navigate to workplan management (will be created in Phase 4)
                  window.location.href = `/rfp/workplan-execution?reportId=${reportId}`;
                }}
              />
            </div>
          )}

          {/* Loading Template */}
          {isLoadingTemplate && !template && (
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 py-4">
              <Loader className="h-5 w-5 mx-auto mb-2 animate-spin" />
              <p>Loading workplan template...</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

